/*
 *  -----------------------------------------------------------------------
 *    <copyright file="control-observation/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { useMutation } from '@apollo/client/react';
import React, { useState } from 'react';
import { UseBooleanActions } from 'react-hanger/array';
import { useBoolean, useTranslation } from '../../../../hooks';
import {
    DELETE_CM_OBSERVATION,
    GET_CM_OBSERVATIONS_AND_ACTIONS,
    RESTORE_CM_OBSERVATION,
} from '../../../../queries/central-monitoring';
import NotificationService from '../../../../utils/notification';
import { ICMObservationAndActionExtended } from '../../../pages/central-monitoring/manage-observations.interface';
import DeleteReasonDialog from '../dialogs/delete-reason-dialog';
import TwoOptionsDialog from '../dialogs/two-options-dialog';
import { ControlObservationProps } from './control-observation.interface';
import {
    DELETE_RISK_OBSERVATION,
    GET_RISK_OBSERVATIONS_AND_ACTIONS,
    RESTORE_RISK_OBSERVATION,
} from '@queries/legacy-risk-assessment';

const ControlObservation = ({ studyId, siteId, datasetDate, isRAM, children }: ControlObservationProps) => {
    const { t } = useTranslation();

    const [currentObservation, setCurrentObservation] = useState(null as ICMObservationAndActionExtended);

    const deleteObservationOpen = useBoolean(false);
    const restoreObservationOpen = useBoolean(false);

    const [deleteCMObsMutation] = useMutation(DELETE_CM_OBSERVATION);
    const [restoreCMObsMutation] = useMutation(RESTORE_CM_OBSERVATION);
    const [deleteRiskObsMutation] = useMutation(DELETE_RISK_OBSERVATION);
    const [restoreRiskObsMutation] = useMutation(RESTORE_RISK_OBSERVATION);

    const refetchQuery = isRAM ? GET_RISK_OBSERVATIONS_AND_ACTIONS : GET_CM_OBSERVATIONS_AND_ACTIONS;
    const deleteMutation = isRAM ? deleteRiskObsMutation : deleteCMObsMutation;
    const restoreMutation = isRAM ? restoreRiskObsMutation : restoreCMObsMutation;

    const handleDialogOpen = (dialog: UseBooleanActions, item: ICMObservationAndActionExtended) => {
        setCurrentObservation(item);
        dialog.setTrue();
    };
    const handleDialogClose = (dialog: UseBooleanActions) => dialog.setFalse();

    const handleDeleteObs = (item: ICMObservationAndActionExtended) => handleDialogOpen(deleteObservationOpen, item);

    const handleRestoreObs = (item: ICMObservationAndActionExtended) => handleDialogOpen(restoreObservationOpen, item);

    const deleteObservation = (reason: string) => {
        deleteMutation({
            variables: {
                id: currentObservation.id,
                reason,
            },
            refetchQueries: [
                {
                    query: refetchQuery,
                    variables: {
                        studyId,
                        siteId,
                        ...(datasetDate?.isCMR ? { timestamp: datasetDate.created } : {}),
                    },
                },
            ],
        })
            .then(() => {
                handleDialogClose(deleteObservationOpen);
                NotificationService.showNotification(t('cm:deleteObsSuccessMessage'));
            })
            .catch(() => NotificationService.showNotification(t('errorMessage')));
    };

    const restoreObservation = (unDeleteActions: boolean = false) => {
        restoreMutation({
            variables: {
                id: currentObservation.id,
                unDeleteActions,
            },
            refetchQueries: [
                {
                    query: refetchQuery,
                    variables: {
                        studyId,
                        siteId,
                        ...(datasetDate?.isCMR ? { timestamp: datasetDate.created } : {}),
                    },
                },
            ],
        })
            .then(() => {
                handleDialogClose(restoreObservationOpen);
                NotificationService.showNotification(t('cm:restoreObsSuccessMessage'));
            })
            .catch(() => NotificationService.showNotification(t('errorMessage')));
    };

    const recordsCount =
        currentObservation?._numberOfActions || currentObservation?._openAction || currentObservation?.actions?.length;
    const dialogWarning = recordsCount ? t('cm:deleteObsDialogWarning', { recordsCount }) : undefined;

    return (
        <>
            {deleteObservationOpen.value && (
                <DeleteReasonDialog
                    title={t('cm:deleteObsDialogHeading', { itemId: currentObservation.recordId })}
                    warning={dialogWarning}
                    handleClose={() => handleDialogClose(deleteObservationOpen)}
                    handleSubmit={deleteObservation}
                />
            )}
            {restoreObservationOpen.value && (
                <TwoOptionsDialog
                    title={t('cm:restoreObsDialogHeading', { itemId: currentObservation.recordId })}
                    submitText={recordsCount ? t('cm:restoreObs') : t('cm:restore')}
                    handleClose={() => handleDialogClose(restoreObservationOpen)}
                    handleSubmit={() => restoreObservation()}
                    extraBntText={recordsCount ? t('cm:restoreBoth') : undefined}
                    extraBntOnClick={recordsCount ? () => restoreObservation(true) : undefined}
                    className="action-dialog-small"
                >
                    {recordsCount ? (
                        <>
                            <p>{t('cm:restoreObsDialogActionsCount', { recordsCount })}</p>
                            <p>{t('cm:restoreObsDialogRestoreActions')}</p>
                        </>
                    ) : (
                        <p>{t('cm:restoreObsDialogWithoutActions')}</p>
                    )}
                </TwoOptionsDialog>
            )}
            {children(handleDeleteObs, handleRestoreObs)}
        </>
    );
};

export default ControlObservation;
